import React, { LegacyRef } from 'react';
import styles from './URLInput.module.scss';
import cn from 'classnames';
import Tooltip, { Overlay } from '@components/Tooltip';
import { CheckIcon, CopyLinkIcon, LoaderIcon, OpenLinkIcon } from '@components/Icons';
import { useTextCopiedTimer } from '@hooks/use-text-copied-timer';

interface URLInputProps {
  value?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSaveClick?: () => void;

  inputRef?: LegacyRef<HTMLInputElement>;

  isChanged?: boolean;
  isSaving?: boolean;
  disabled?: boolean;
  precedeText?: string;
  urlBuilder?: (input: string) => string;
  copyLink?: boolean;
  openLink?: boolean;

  hasError?: boolean;

  precedeClassName?: string;
  inputClassName?: string;
}

export default function URLInput({
  value,
  placeholder = 'Add a URL',
  onChange,
  onSaveClick,
  onKeyDown,
  precedeText,
  urlBuilder = (input) => input,
  copyLink,
  openLink,
  disabled,
  isChanged = false,
  isSaving: saving = false,
  hasError = false,
  inputRef,
  precedeClassName,
  inputClassName,
}: URLInputProps) {
  const [linkCopied, copyLinkFn] = useTextCopiedTimer(urlBuilder(value));

  function onCopyLinkClick() {
    copyLinkFn();
  }

  function onOpenLinkClick() {
    const url = urlBuilder(value);
    window.open(url, '_blank');
  }

  const canSave = isChanged && value.length > 0 && !saving;
  const showLinkButtons = !saving && !disabled && !canSave && !!value.length;
  const disableUrl = saving || !!disabled;

  return (
    <>
      {precedeText && <div className={cn(styles.precede, precedeClassName)}>{precedeText}</div>}
      <input
        className={cn(
          styles.textInput,
          inputClassName,
          isChanged && styles.dirtyInput,
          hasError && styles.error,
        )}
        type="text"
        required
        ref={inputRef}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={disableUrl}
      />
      {(openLink || copyLink) && (
        <div className={styles.btnContainer}>
          {showLinkButtons && (
            <>
              {openLink && (
                <Tooltip placement="bottom" overlay={<Overlay>Open Link</Overlay>}>
                  <OpenLinkIcon
                    width="17"
                    height="17"
                    className={styles.iconBtn}
                    tabIndex={0}
                    onClick={onOpenLinkClick}
                    hoverOpacity
                  />
                </Tooltip>
              )}
              {copyLink && (
                <Tooltip
                  placement="bottom"
                  overlay={<Overlay>{linkCopied ? 'Copied!' : 'Copy Link'}</Overlay>}
                >
                  <CopyLinkIcon
                    width="17"
                    height="17"
                    className={styles.iconBtn}
                    tabIndex={0}
                    onClick={onCopyLinkClick}
                    hoverOpacity
                  />
                </Tooltip>
              )}
            </>
          )}
          {saving && <LoaderIcon />}
          {canSave && onSaveClick && (
            <CheckIcon width="14" className={styles.iconBtn} tabIndex={0} onClick={onSaveClick} />
          )}
        </div>
      )}
    </>
  );
}
